import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";

// =====================================================
// CHANGE THIS NUMBER ANYTIME YOU WANT
// Country code + number, without +, spaces or hyphens
// Example: +91 7799551572 → 917799551572
// =====================================================
const WHATSAPP_NUMBER = "917799551572";

const CONTACTS = [
  {
    k: "Email",
    v: "hello@parallel.studio",
    href: "mailto:hello@parallel.studio",
  },
  {
    k: "WhatsApp",
    v: "+91 77995 51572",
    href: `https://wa.me/${WHATSAPP_NUMBER}`,
  },
  {
    k: "Instagram",
    v: "@parallel.studio",
    href: "https://instagram.com",
  },
  {
    k: "Base",
    v: "Mumbai / Bengaluru / Hyderabad / Remote",
  },
];

const inputCls =
  "w-full bg-ink border border-line px-4 py-3.5 font-mono text-sm text-cream placeholder:text-dim focus:outline-none focus:border-acid transition-colors duration-300";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-smoke mb-2">
      {label}
    </span>
    {children}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    track: "",
    portfolio: "",
    message: "",
  });

  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, track: e.detail }));

    window.addEventListener("parallel:track", h);

    return () => window.removeEventListener("parallel:track", h);
  }, []);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const whatsappMessage = `
🔔 NEW PARALLEL LEAD

Source: Parallel Landing Page

Name: ${form.name}
Email: ${form.email}
Phone / WhatsApp: ${form.phone || "Not provided"}
Preferred Track: ${form.track || "Not specified"}
Portfolio / Instagram: ${form.portfolio || "Not provided"}

Message:
${form.message || "No message provided"}

— Parallel Creative Studio
`.trim();

      const whatsappURL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
        whatsappMessage
      )}`;

      // Open the customer's WhatsApp with the enquiry pre-filled
      window.open(whatsappURL, "_blank", "noopener,noreferrer");

      setSent(true);

      toast.success("WhatsApp opened with your enquiry.");

    } catch (err) {
      toast.error("Could not open WhatsApp. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="apply"
      data-testid="contact-section"
      className="bg-obsidian border-t border-line py-28 sm:py-40"
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-10">
        <SectionHead
          num="17"
          eyebrow="Apply / Contact"
          title={
            <>
              Start the conversa<span className="text-acid">tion.</span>
            </>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <p className="text-base sm:text-lg text-smoke leading-relaxed max-w-md">
              Tell us where you are and where you want to be. A creative
              director — not a sales rep — reads every application.
            </p>

            <div className="mt-12 space-y-px bg-line border border-line">
              {CONTACTS.map((c) => (
                <div
                  key={c.k}
                  className="bg-obsidian px-6 py-5 flex items-center justify-between group"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">
                    {c.k}
                  </span>

                  {c.href ? (
                    <a
                      data-testid={`contact-${c.k.toLowerCase()}-link`}
                      href={c.href}
                      target={
                        c.href.startsWith("http") ? "_blank" : undefined
                      }
                      rel="noopener noreferrer"
                      className="font-mono text-xs uppercase tracking-[0.15em] text-cream hover:text-acid transition-colors duration-300 flex items-center gap-2"
                    >
                      {c.v}
                      <ArrowUpRight
                        className="w-3.5 h-3.5"
                        strokeWidth={1.5}
                      />
                    </a>
                  ) : (
                    <span className="font-mono text-xs uppercase tracking-[0.15em] text-smoke">
                      {c.v}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div
                data-testid="enquiry-success"
                className="border border-acid bg-ink p-10 sm:p-14 h-full flex flex-col justify-center acid-glow"
              >
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-acid">
                  {"//"} WhatsApp
                </span>

                <h3 className="mt-6 font-head text-3xl sm:text-4xl font-bold uppercase tracking-tight text-cream">
                  Enquiry ready.
                </h3>

                <p className="mt-4 text-smoke text-sm sm:text-base leading-relaxed max-w-md">
                  Your enquiry has been prepared in WhatsApp. Please press
                  Send in WhatsApp to complete your enquiry.
                </p>

                <button
                  data-testid="enquiry-again-btn"
                  onClick={() => {
                    setSent(false);
                    setForm({
                      name: "",
                      email: "",
                      phone: "",
                      track: "",
                      portfolio: "",
                      message: "",
                    });
                  }}
                  className="mt-10 self-start border border-line text-cream font-mono text-xs uppercase tracking-[0.2em] px-6 py-3 hover:border-acid hover:text-acid transition-colors duration-300"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="enquiry-form"
                className="grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                <Field label="Full Name *">
                  <input
                    data-testid="enquiry-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Your name"
                    className={inputCls}
                  />
                </Field>

                <Field label="Email *">
                  <input
                    data-testid="enquiry-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    className={inputCls}
                  />
                </Field>

                <Field label="Phone / WhatsApp">
                  <input
                    data-testid="enquiry-phone-input"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="+91 ..."
                    className={inputCls}
                  />
                </Field>

                <Field label="Preferred Track">
                  <select
                    data-testid="enquiry-track-select"
                    value={form.track}
                    onChange={set("track")}
                    className={`${inputCls} appearance-none`}
                  >
                    <option value="">Not sure yet</option>
                    <option value="basic">
                      Agency Basic — ₹35,000
                    </option>
                    <option value="advanced">
                      Advanced Creative / AI — ₹1,50,000
                    </option>
                  </select>
                </Field>

                <div className="sm:col-span-2">
                  <Field label="Portfolio / Instagram URL">
                    <input
                      data-testid="enquiry-portfolio-input"
                      value={form.portfolio}
                      onChange={set("portfolio")}
                      placeholder="instagram.com/you or a link to your work"
                      className={inputCls}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="Message">
                    <textarea
                      data-testid="enquiry-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Where are you now, and what do you want to make?"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <button
                    data-testid="enquiry-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="bg-acid text-obsidian font-mono text-xs font-bold uppercase tracking-[0.2em] px-10 py-4 hover:bg-acid-hover hover:scale-[1.02] active:scale-[0.98] transition-transform duration-300 disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {loading ? "Opening WhatsApp..." : "Submit Application"}
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>

        <div className="mt-24 pt-8 border-t border-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <span className="font-display font-extrabold text-lg uppercase tracking-tight text-cream">
            PARA<span className="text-acid">{"//"}</span>EL
          </span>

          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">
            © Parallel Creative Studio — By Athrv Co.
          </span>

          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-dim">
            2026 → A more interesting tomorrow.
          </span>
        </div>
      </div>
    </section>
  );
};